<template>
    <m-pageframe :title="title">
        <v-container v-if="template != null">
            <link-back
                class="mb-4"
                text="All Automated Messages"
                :to="back_route"
            />

            <!-- Alerts -->
            <v-alert
                v-if="show_red_chirp_staff_no_edit_in_auto_mode"
                border="left"
                colored-border
                color="primary"
                elevation="0"
                class="my-4 shaded-light"
            >
                <div class="d-flex align-center justify-space-between">
                    <div>
                        <v-icon
                            color="primary"
                            class="ml-2 mr-3"
                            aria-hidden="true"
                        >
                            mdi-pencil-off
                        </v-icon>
                        <strong>Editing Disabled:</strong> RedChirp staff may not edit an automated message that is in
                        automatic mode
                    </div>
                    <v-btn
                        color="primary"
                        elevation="0"
                        outlined
                        @click="onClickPlaceIntoManualMode"
                    >
                        <v-icon
                            aria-hidden="true"
                            left
                        >mdi-pencil</v-icon>
                        Place into Manual Mode
                    </v-btn>
                </div>
            </v-alert>

            <v-alert
                v-if="template && template.is_template_context_deprecated"
                border="left"
                colored-border
                color="error"
                elevation="2"
                type="error"
                class="mt-4"
            >
                <p class="mb-2">
                    <strong>Deprecation Notice:</strong> This message is no longer being actively supported and will be
                    fully removed in the future.
                </p>
                <p class="mb-0">
                    It is recommended that you disable this message. If you have questions or need assistance, please <a
                        href="#"
                        @click.prevent="contactSupport"
                    >contact RedChirp support</a>.
                </p>
            </v-alert>

            <m-beta-alert
                v-if="template.template_context == 'order-placed-request-opt-in'"
                code="beta-order-placed-request-opt-in"
            />

            <m-beta-alert
                v-if="template.template_context == 'order-placed-request-order-confirmation'"
                code="beta-order-placed-request-order-confirmation"
            />

            <!-- Context-Specific Message Info + FAQ -->
            <shipping-message-info
                v-if="is_second_generation_shipping_message"
                :template_context="template.template_context"
            />

            <birthday-message-info
                v-if="['birthday-message', 'birthday-month-message'].includes(template.template_context)"
                :template_context="template.template_context"
            />

            <reservation-message-info
                v-if="is_reservation_message"
                :template_context="template.template_context"
                :is_ecellar_enabled="is_ecellar_enabled"
            />

            <wine-club-message-info
                v-if="['wine-club-new-membership', 'wine-club-join-follow-up'].includes(template.template_context)"
                :template_context="template.template_context"
            />

            <order-placed-message-info
                v-if="['order-placed', 'order-placed-request-opt-in', 'order-placed-request-order-confirmation', 'order-fulfilled-follow-up'].includes(template.template_context)"
                :template_context="template.template_context"
                @scroll-to-faq="scrollToAndHighlightElement"
            />

            <other-message-info
                :template_context="template.template_context"
                :custom_webhook_url="custom_webhook_url"
                :list_drip_delay="template.list_drip_delay"
                :list_drip_list_internal_label="template.list_drip_list_internal_label"
                :commerce7_email_when_will_it_send="commerce7_email_when_will_it_send"
            />

            <auto-message-faq
                :template_context="template.template_context"
                :is_second_generation_shipping_message="is_second_generation_shipping_message"
                :shipping_trigger_concept="shipping_trigger_concept"
            />

            <!-- Form Controls -->
            <m-edit-text
                v-if="template.template_context === 'wine-club-join-follow-up'"
                label="Delay (days between join date and this message being sent)"
                v-model="template.param_delay_days"
                @input="save"
                integer_positive
                :read_only="form_is_readonly"
            />

            <m-edit-text
                v-if="template.template_context === 'order-fulfilled-follow-up'"
                label="Delay (days between the earliest fulfillment on this order and this message being sent)"
                v-model="template.param_delay_days"
                @input="save"
                integer_positive_non_zero
                :read_only="form_is_readonly"
            />

            <div v-if="data_cleaning_certification_status !== null">
                <!-- Warning when not certified -->
                <v-alert
                    v-if="!data_cleaning_certification_status"
                    border="left"
                    colored-border
                    color="primary"
                    elevation="2"
                    type="warning"
                    class="mb-4"
                >
                    <div class="d-flex align-center">
                        <div class="flex-grow-1">
                            <p class="mb-2">
                                <strong>Certification Required:</strong> Birthday messages will not be sent until you
                                certify your data cleaning rules.
                            </p>
                            <p class="mb-0">
                                These rules ensure that birthday messages are not sent to customers that may have
                                placeholder dates input as their birthday.
                            </p>
                        </div>
                        <div class="ml-4">
                            <v-btn
                                color="primary"
                                @click="onClickDataCleaningSettings"
                                small
                            >
                                <v-icon
                                    left
                                    small
                                >mdi-alert</v-icon>
                                Certify Data Cleaning Rules
                            </v-btn>
                        </div>
                    </div>
                </v-alert>

                <!-- Info when certified (less obtrusive) -->
                <v-alert
                    v-else
                    border="left"
                    colored-border
                    color="success"
                    elevation="1"
                    type="success"
                    class="mb-4"
                    dense
                >
                    <div class="d-flex align-center">
                        <div class="flex-grow-1">
                            <span class="text-body-2">
                                Data cleaning rules configured and certified for birthday data
                            </span>
                        </div>
                        <div class="ml-3">
                            <v-btn
                                @click="onClickDataCleaningSettings"
                                outlined
                                mall
                                class="text-caption"
                            >
                                <v-icon
                                    left
                                    small
                                >mdi-cog</v-icon>
                                Configure Rules
                            </v-btn>
                        </div>
                    </div>
                </v-alert>
            </div>

            <v-text-field
                label="Organization"
                :value="organization_label"
                readonly
                disabled
            />

            <v-text-field
                label="Template Type"
                :value="template.template_context"
                readonly
                disabled
            />

            <v-select
                v-if="['birthday-message', 'birthday-month-message'].includes(template.template_context)"
                label="Message Type"
                :items="filteredMessageTypeChoices"
                v-model="template.message_type"
                :disabled="form_is_readonly"
                @change="save"
            />

            <m-edit-text
                v-if="template.list_drip_list_id != null"
                label="Time to wait after a member is added to a list before sending"
                v-model="template.list_drip_delay"
                duration
                :read_only="form_is_readonly"
                @input="save"
            />

            <m-edit-text
                label="Template Label"
                v-model="template.template_label"
                autocomplete="off"
                :read_only="form_is_readonly"
                @input="save"
            />

            <order-confirmation-form-selector
                :template="template"
                :order_confirmation_form_choices="order_confirmation_form_choices"
                :read_only="form_is_readonly"
                @update:orderConfirmationSettingsId="onUpdateOrderConfirmationSettingsId"
                @view-form="onClickViewOrderConfirmationForm"
                @create-form="onClickCreateOrderConfirmationForm"
            />

            <birthday-message-settings
                :template="template"
                :read_only="form_is_readonly"
                @input="save"
            />

            <mailchimp-campaign-manager
                :template="template"
                :mailchimp_campaigns="mailchimp_campaigns"
                :read_only="form_is_readonly"
                @add-campaign="onAddCRMEmailCampaignId"
                @remove-campaign="onClickRemoveCRMEmailCampaign"
                @update:emailEventTriggers="onUpdateEmailEventTriggers"
            />

            <conditional-messages
                :template="template"
                :supports_conditions="supports_conditions"
                :condition_profiles="condition_profiles"
                :integration_crm_summaries="integration_crm_summaries"
                :send_from_choices="send_from_choices"
                :link_shortener_host_choices="link_shortener_host_choices"
                :organization_account="organization_account"
                :read_only="form_is_readonly"
                @save="onEditConditionSave"
                @update-condition="onChangeConditionMessageCtrls"
                @update-default="onChangeDefaultMessageCtrls"
                @move-up="onClickMoveConditionalUp"
                @move-down="onClickMoveConditionalDown"
                @delete="onClickDeleteConditional"
            />

            <send-during-hours-section
                :template="template"
                :send_during_timezone="send_during_timezone"
                :read_only="form_is_readonly"
                @input="onInputSendDuring"
            />

            <!-- Additional Instructions -->
            <klaviyo-webhook-instructions
                :template="template"
                :klaviyo_webhook_url="klaviyo_webhook_url"
            />

            <custom-automated-message-instructions
                :template="template"
                :custom_webhook_url="custom_webhook_url"
            />

            <!-- Recipient Exclusion -->
            <div class="mt-8">
                <strong>Excluded Recipients</strong>
                <p>You have excluded {{ count_excluded_endpoints }} from this automated message.</p>
                <div class="text-right">
                    <v-btn
                        :disabled="form_is_readonly"
                        @click="onClickEditExcludedRecipients"
                    >
                        Edit Excluded Recipients...
                    </v-btn>
                </div>
            </div>

            <!-- Consent Settings -->
            <div>
                <strong>Consent</strong>
                <v-checkbox
                    label="Only send to recipients that have informational consent on file in RedChirp"
                    v-model="template.require_grant_consent_information"
                    :disabled="form_is_readonly"
                    hide-details
                    @change="doSave"
                />
            </div>

            <v-checkbox
                label="Only send to recipients that have promotional consent on file in RedChirp"
                v-model="template.require_grant_consent_promotion"
                :disabled="form_is_readonly"
                @change="doSave"
            />

            <m-edit-text
                label="Only send to recipients that have not been sent this message in the specified number of days"
                v-model="template.filter_days_since_received_this_message"
                integer_positive
                :read_only="form_is_readonly"
                @input="doSave"
            />

            <!-- Dialogs -->
            <rc-info-dialog
                v-model="show_order_confirmation_setup_dialog"
                title="Order Confirmation Setup Required"
            >
                <template #default>
                    <div class="text-body-1 mb-4">
                        <strong>This automated message requires a 2-step setup:</strong>
                    </div>

                    <v-timeline
                        class="mb-4"
                        align-top
                        dense
                    >
                        <v-timeline-item
                            color="primary"
                            small
                            fill-dot
                        >
                            <template #icon>
                                <span class="white--text font-weight-bold">1</span>
                            </template>
                            <div class="pb-3">
                                <div class="font-weight-medium mb-1">Create an Order Confirmation Form</div>
                                <div class="text-body-2 text--secondary">
                                    Design a customized form where customers can review and confirm their order details.
                                </div>
                            </div>
                        </v-timeline-item>

                        <v-timeline-item
                            color="primary"
                            small
                            fill-dot
                        >
                            <template #icon>
                                <span class="white--text font-weight-bold">2</span>
                            </template>
                            <div>
                                <div class="font-weight-medium mb-1">Link the Form to This Automated Message</div>
                                <div class="text-body-2 text--secondary">
                                    Once published, select your form from the dropdown on this page to complete the
                                    setup.
                                </div>
                            </div>
                        </v-timeline-item>
                    </v-timeline>

                    <v-alert
                        type="info"
                        class="mb-0"
                        outlined
                        dense
                    >
                        <div class="text-body-2">
                            When a customer places an order, they'll receive a text message with a unique link to your
                            Order Confirmation
                            Form based on the conditions you've set on this page.
                        </div>
                    </v-alert>
                </template>

                <template #actions>
                    <v-btn
                        text
                        @click="onClickStayOnAutomatedMessage"
                    >
                        Stay on This Page
                    </v-btn>

                    <v-spacer />

                    <v-btn
                        color="primary"
                        @click="onClickCreateOrderConfirmationForm"
                    >
                        Create Order Confirmation Form
                        <v-icon
                            aria-hidden="true"
                            right
                        >mdi-arrow-right</v-icon>
                    </v-btn>
                </template>
            </rc-info-dialog>
        </v-container>
    </m-pageframe>
</template>

<script>
import _ from 'lodash';
import Time from '@/js/time.js';
import Validate from '@/js/validate.js';

import GuidedValidationMixin from '@/mixins/guided-validation.js';
import ShippingMessageInfo from '@/components/auto-messages/contexts/shipping-message-info.vue';
import BirthdayMessageInfo from '@/components/auto-messages/contexts/birthday-message-info.vue';
import ReservationMessageInfo from '@/components/auto-messages/contexts/reservation-message-info.vue';
import WineClubMessageInfo from '@/components/auto-messages/contexts/wine-club-message-info.vue';
import OrderPlacedMessageInfo from '@/components/auto-messages/contexts/order-placed-message-info.vue';
import OtherMessageInfo from '@/components/auto-messages/contexts/other-message-info.vue';
import AutoMessageFaq from '@/components/auto-messages/auto-message-faq.vue';
import ConditionalMessages from '@/components/auto-messages/conditional-messages.vue';
import OrderConfirmationFormSelector from '@/components/auto-messages/order-confirmation-form-selector.vue';
import SendDuringHoursSection from '@/components/auto-messages/send-during-hours-section.vue';
import BirthdayMessageSettings from '@/components/auto-messages/birthday-message-settings.vue';
import MailchimpCampaignManager from '@/components/auto-messages/mailchimp-campaign-manager.vue';
import KlaviyoWebhookInstructions from '@/components/auto-messages/klaviyo-webhook-instructions.vue';
import CustomAutomatedMessageInstructions from '@/components/auto-messages/custom-automated-message-instructions.vue';

export default {
    mixins: [GuidedValidationMixin],

    components: {
        ShippingMessageInfo,
        BirthdayMessageInfo,
        ReservationMessageInfo,
        WineClubMessageInfo,
        OrderPlacedMessageInfo,
        OtherMessageInfo,
        AutoMessageFaq,
        ConditionalMessages,
        OrderConfirmationFormSelector,
        SendDuringHoursSection,
        BirthdayMessageSettings,
        MailchimpCampaignManager,
        KlaviyoWebhookInstructions,
        CustomAutomatedMessageInstructions,
    },

    data() {
        return {
            organization_label: null,
            template: null,

            send_from_choices: [],
            send_during_timezone: null,
            supports_conditions: false,
            condition_profiles: [],
            link_shortener_hosts: [],
            organization_account: null,
            count_excluded_endpoints: 0,
            integration_crm_summaries: [],
            data_cleaning_certification_status: null,
            certification_poll_interval: null,
            show_red_chirp_staff_no_edit_in_auto_mode: false,
            order_confirmation_form_choices: [],
            show_order_confirmation_setup_dialog: false,

            upgradeable_to_message_pattern_context: null,

            // Add Time and Validate to make them available in the template
            Time: Time,
            Validate: Validate,
        }
    },

    mounted() {
        this.update();
    },

    beforeDestroy() {
        // Clean up polling interval to prevent memory leaks
        this.stopCertificationPolling();
    },

    methods: {
        update() {
            let params = {
                'organization_id': this.$route.params.organization_id,
                'template_subject_id': this.$route.params.template_subject_id,
                'template_context': this.$route.params.template_context,
            };

            this.$rc.showSpinner();

            this.$rc.getAutoMessageTemplateProfile(
                params,

                (data) => {
                    this.$rc.hideSpinner();

                    this.show_red_chirp_staff_no_edit_in_auto_mode = data.show_red_chirp_staff_no_edit_in_auto_mode;
                    this.organization_label = data.organization_label;
                    this.template = data.template;
                    this.send_from_choices = data.send_from_choices;
                    this.send_during_timezone = data.send_during_timezone;
                    this.supports_conditions = data.supports_conditions;

                    // Load data cleaning certification status for birthday templates
                    this.loadDataCleaningCertificationStatus();
                    this.condition_profiles = data.condition_profiles;
                    this.integration_crm_summaries = data.integration_crm_summaries;
                    this.link_shortener_hosts = data.link_shortener_hosts;
                    this.organization_account = data.organization_account;
                    this.count_excluded_endpoints = data.count_excluded_endpoints;
                    this.upgradeable_to_message_pattern_context = data.upgradeable_to_message_pattern_context;

                    this.numberConditions();

                    this.loadPublishedOrderConfirmationForms();

                    this.$nextTick(() => {
                        this.handleHighlightOnLoad();
                    });
                },

                () => {
                    this.$rc.hideSpinner();
                    this.$rc.alert('Error', 'Unable to complete API call');
                },
            );
        },

        handleHighlightOnLoad() {
            const highlight_target = this.$route.query.highlight;
            if (highlight_target) {
                this.scrollToAndHighlightElement(highlight_target);
                this.$router.replace({ query: {} });
            }
        },

        save: _.debounce(function () {
            this.doSave();
        }, 1000),

        doSave() {
            // just in case bad actor got around the readonly inputs or an individual input is not set up properly in the future
            if (this.form_is_readonly) {
                const error_message = this.show_red_chirp_staff_no_edit_in_auto_mode ?
                    'RedChirp staff may not edit an automated message that is in automatic mode' :
                    'Editing is disabled for this message';

                this.$rc.alert('Error', error_message);
                this.update();
                return;
            }

            // Create a copy of the template for saving
            let template_to_save = { ...this.template };

            // For non-birthday message types, remove the message_type field to leave it unset/null
            if (template_to_save.template_context !== 'birthday-message' &&
                template_to_save.template_context !== 'birthday-month-message') {
                delete template_to_save.message_type;
            }

            this.$rc.putAutoMessageTemplate(
                template_to_save,

                (data) => {
                    this.template = data.template;
                    this.numberConditions();
                },

                () => {
                    this.$rc.alert('Error', 'Unable to save template');
                },
            );
        },

        onChangeDefaultMessageCtrls(new_value) {
            this.updateTemplateFromMessageCtrls(new_value);
        },

        updateTemplateFromMessageCtrls(new_value) {
            this.template.send_mode = new_value.send_mode;
            this.template.send_from = new_value.send_from;
            this.template.link_shortener_host = new_value.link_shortener_host;
            this.template.message_patterns = new_value.message_patterns;
            this.template.blob_ids = new_value.blob_ids;
            this.template.vcard_id = new_value.vcard_id;
            this.template.time_delay_send_after_enqueue = new_value.time_delay_send_after_enqueue;
            this.template.time_delay_expire_after_trigger = new_value.time_delay_expire_after_trigger;

            this.save();
        },

        checkDataCleaningCertification(callback) {
            this.$rc.rest({
                'endpoint': 'GET: /api/v1/organization-data-cleaning-rules/:organization_id',
                'params': {
                    'organization_id': this.$route.params.organization_id,
                },
                'on_success': (data) => {
                    // Check the is_certified field from the data cleaning rules
                    callback(data.is_certified === true);
                },
                'on_error': () => {
                    // If no rules exist yet, they're not certified
                    callback(false);
                }
            });
        },

        onClickPlaceIntoManualMode() {
            this.$rc.confirm({
                'title': 'Put Into Manual Mode',
                'body': 'This message will be placed into manual mode (including all conditional messages).  Proceed?',
                'btn_text_ok': 'Proceed: Place Into Manual Mode',
                'btn_text_cancel': 'Cancel',
                'on_ok': () => {
                    this.doPlaceIntoManualMode();
                },
            });
        },

        doPlaceIntoManualMode() {
            this.$rc.rest({
                'spinner': true,
                'endpoint': 'PUT: /api/v1/auto-messages/templates/put-into-manual-mode',

                'params': {
                    'organization_id': this.$route.params.organization_id,
                    'template_context': this.$route.params.template_context,
                    'template_subject_id': this.$route.params.template_subject_id
                },

                'on_success': (data) => {
                    this.update();
                },

                'on_error': 'Unable to place message into manual mode',
            });
        },

        loadDataCleaningCertificationStatus() {
            // Only load for birthday message templates
            if (this.template && (this.template.template_context === 'birthday-message' || this.template.template_context === 'birthday-month-message')) {
                this.checkDataCleaningCertification((is_certified) => {
                    this.data_cleaning_certification_status = is_certified;
                });
            }
        },

        onChangeConditionMessageCtrls(new_value) {
            let index = new_value.condition_index;

            this.template.conditions[index].send_mode = new_value.send_mode;
            this.template.conditions[index].send_from = new_value.send_from;
            this.template.conditions[index].link_shortener_host = new_value.link_shortener_host;
            this.template.conditions[index].message_patterns = new_value.message_patterns;
            this.template.conditions[index].custom_time_delay_send_after_enqueue = new_value.custom_time_delay_send_after_enqueue;
            this.template.conditions[index].custom_time_delay_expire_after_trigger = new_value.custom_time_delay_expire_after_trigger;

            this.save();
        },

        onClickAddConditionMessage() {
            let condition = {
                'label': 'Conditional message',
                'constraints': [],
                'send_mode': 'manual',
                'send_from': this.template.send_from,
                'message_patterns': this.template.message_patterns,
                'condition_index': -1,
            };

            this.$refs['condition-edit-dialog'].showDialog(condition, this.condition_profiles, this.integration_crm_summaries);
        },

        onEditConditionSave(condition) {
            let index = condition.condition_index;

            if (index == -1) {
                this.template.conditions.push(condition);
                this.numberConditions();
                this.save();
                return;
            }

            this.template.conditions[index] = condition;
            this.$forceUpdate();
            this.save();
        },

        getSelectedConstraintValue(conditional, type) {
            let constraints = conditional.constraints;

            for (let i = 0; i < constraints.length; i++) {
                if (constraints[i].type == type)
                    return constraints[i].values;
            }

            return [];
        },

        onClickEditConditions(condition) {
            this.$refs['condition-edit-dialog'].showDialog(condition, this.condition_profiles, this.integration_crm_summaries);
        },

        onClickDeleteConditional(condition) {
            this.$rc.confirm(
                'Delete condition',
                'Permenantly delete this conditional message?',
                [
                    { 'text': 'Cancel', 'value': 'cancel' },
                    { 'text': 'Delete', 'value': 'confirm' },
                ],

                (response) => {
                    if (response != 'confirm') return;

                    let index = condition.condition_index;
                    this.template.conditions.splice(index, 1);
                    this.$forceUpdate();
                    this.save();
                }
            );
        },

        onClickMoveConditionalUp(condition) {
            let index = condition.condition_index;
            if (index <= 0) return;


            let tmp = this.template.conditions[index - 1];
            this.template.conditions[index - 1] = this.template.conditions[index];
            this.template.conditions[index] = tmp;
            this.numberConditions();

            this.$forceUpdate();
            this.save();
        },

        onClickMoveConditionalDown(condition) {
            let index = condition.condition_index;
            if (!(index + 1 <= this.template.conditions.length - 1)) return;

            let tmp = this.template.conditions[index + 1];
            this.template.conditions[index + 1] = this.template.conditions[index];
            this.template.conditions[index] = tmp;
            this.numberConditions();

            this.$forceUpdate();
            this.save();
        },

        numberConditions() {
            for (let i = 0; i < this.template.conditions.length; i++) {
                this.template.conditions[i].condition_index = i;
            }
        },

        onClickAddMailchimpCampaign() {
            this.$refs['auto-message-add-crm-email-dailog'].showDialog();
        },

        onAddCRMEmailCampaignId(email_campaign_id) {
            if (this.template.template_context == 'mailchimp') this.template.email_content_type = 'mailchimp';

            this.template.email_campaign_ids.push(email_campaign_id);
            this.doSave();
        },

        onClickRemoveCRMEmailCampaign(email_campaign_id) {
            if (this.template.template_context == 'mailchimp') this.template.email_content_type = 'mailchimp';

            for (let i = this.template.email_campaign_ids.length - 1; i >= 0; i--) {
                if (this.template.email_campaign_ids[i] == email_campaign_id)
                    this.template.email_campaign_ids.splice(i, 1);
            }

            this.doSave();
        },

        onClickEditExcludedRecipients() {
            this.$router.push({
                'name': 'auto-messages-excluded-recipients',
                'params': {
                    'organization_id': this.$route.params.organization_id,
                    'template_context': this.$route.params.template_context,
                    'template_subject_id': this.$route.params.template_subject_id
                }
            });
        },

        onUpdateEmailEventTriggers(value) {
            this.template.email_event_triggers = value;
            this.save();
        },

        onClickDataCleaningSettings() {
            // Open data cleaning settings in a new tab
            const route = this.$router.resolve({
                'name': 'settings-organization-data-cleaning-rules',
                'params': {
                    'organization_id': this.$route.params.organization_id
                }
            });
            window.open(route.href, '_blank');

            // Start polling for certification status updates if not currently certified
            if (this.data_cleaning_certification_status === false || this.data_cleaning_certification_status === null) {
                this.startCertificationPolling();
            }
        },

        startCertificationPolling() {
            // Don't start polling if already polling
            if (this.certification_poll_interval) {
                return;
            }


            // Poll every 3 seconds for certification status updates
            this.certification_poll_interval = setInterval(() => {
                this.pollCertificationStatus();
            }, 3000);

            // Stop polling after 5 minutes to avoid infinite polling
            setTimeout(() => {
                this.stopCertificationPolling();
            }, 5 * 60 * 1000);
        },

        stopCertificationPolling() {
            if (this.certification_poll_interval) {
                //console.log('Stopping certification status polling');
                clearInterval(this.certification_poll_interval);
                this.certification_poll_interval = null;
            }
        },

        pollCertificationStatus() {
            // Only poll for birthday message templates
            if (this.template && (this.template.template_context === 'birthday-message' || this.template.template_context === 'birthday-month-message')) {
                this.checkDataCleaningCertification((is_certified) => {
                    const previous_status = this.data_cleaning_certification_status;
                    this.data_cleaning_certification_status = is_certified;

                    // If certification status changed from false/null to true, stop polling
                    if ((previous_status === false || previous_status === null) && is_certified === true) {
                        //console.log('Certification detected! Stopping poll.');
                        this.stopCertificationPolling();

                    }
                });
            }
        },

        loadPublishedOrderConfirmationForms(alert_if_none_found = true) {
            this.$rc.rest({
                spinner: false,
                endpoint: 'POST: /api/v1/order-confirmation/settings/search',
                params: {
                    organization_id: this.$route.params.organization_id,
                    is_published: true,
                    is_archived: false,
                },
                on_success: data => {
                    this.order_confirmation_form_choices = data.results.map(form => ({
                        text: form.internal_label,
                        value: form.order_confirmation_settings_id,
                    }));

                    const order_confirmation_settings_id = this.$route.query.order_confirmation_settings_id;

                    // auto-link if query param present and either:
                    // 1. Template doesn't have a linked form (new template case)
                    // 2. Template exists but linked to different form (update case)
                    if (order_confirmation_settings_id &&
                        this.template.order_confirmation_settings_id !== order_confirmation_settings_id) {

                        this.template.order_confirmation_settings_id = order_confirmation_settings_id;

                        this.$router.replace({
                            name: this.$route.name,
                            params: this.$route.params,
                            query: {},
                        });

                        this.save();

                        this.$nextTick(() => {
                            this.$rc.snackbar('Automated message created and order confirmation form linked!');
                        });
                    } else if (
                        alert_if_none_found &&
                        this.template?.template_context === 'order-placed-request-order-confirmation' &&
                        !this.order_confirmation_form_choices.length
                    ) {
                        this.show_order_confirmation_setup_dialog = true;
                    }
                },
                on_error: 'Unable to load order confirmation forms',
            });
        },

        onClickViewOrderConfirmationForm() {
            const organization_id = this.$route.params.organization_id;

            this.$router.push({
                name: 'order-confirmation-edit',
                params: {
                    organization_id,
                    order_confirmation_settings_id: this.template.order_confirmation_settings_id
                },
            });
        },

        onClickCreateOrderConfirmationForm() {
            const organization_id = this.$route.params.organization_id;

            //Go straight to the order confirmation template selection page 
            this.$router.push({
                name: 'order-confirmation-template-selection',
                params: { organization_id },
            });
        },

        onClickStayOnAutomatedMessage() {
            // refresh the order confirmation forms in case they're coming back from creating one
            this.loadPublishedOrderConfirmationForms(false);

            this.show_order_confirmation_setup_dialog = false;
        },

        contactSupport() {
            this.$rc.contactSupport('Deprecated Automated Message Template');
        },

        scrollToOrderPlacedMultipleFAQ() {
            this.scrollToAndHighlightElement('orderPlacedMultipleFaq');
        },

        onUpdateOrderConfirmationSettingsId(value) {
            this.template.order_confirmation_settings_id = value;
            this.save();
        },

        onInputSendDuring(value) {
            this.template.send_during = value;
            this.save();
        },
    },

    computed: {
        form_is_readonly() {
            return !!this.show_red_chirp_staff_no_edit_in_auto_mode;
        },

        filteredMessageTypeChoices() {
            // Filter out the "p2p" option from the async_message_type enum
            return this.$rc.enumChoices('async_message_type', false)
                .filter(choice => choice.value !== 'p2p');
        },

        title() {
            let ol = this.organization_label;
            let template = this.template;

            if (ol == null || template == null)
                return "Template Settings";

            return 'Settings: ' + template.template_label;
        },

        commerce7_email_when_will_it_send() {
            if (this.template == null) return null;

            let template = this.template;

            if (template.template_context === 'commerce-7-transaction-email-order-confirmation-club-ship') return { 'label': 'Order Confirmation Club Ship', 'fourteen_day_limit': false };
            if (template.template_context === 'commerce-7-transaction-email-order-confirmation-ship') return { 'label': 'Order Confirmation Ship', 'fourteen_day_limit': false };
            if (template.template_context === 'commerce-7-transaction-email-club-ship-two-weeks') return { 'label': 'Club Shipment 2 Weeks Away', 'fourteen_day_limit': false };
            if (template.template_context === 'commerce-7-transaction-email-club-ship-two-days') return { 'label': 'Club Shipment 2 Days Away', 'fourteen_day_limit': false };
            if (template.template_context === 'commerce-7-transaction-email-club-credit-card-decline') return { 'label': 'Club Member Shipment Credit Card Decline', 'fourteen_day_limit': true };
            if (template.template_context === 'commerce-7-transaction-email-order-club-pickup') return { 'label': 'Order Confirmation Club Pickup', 'fourteen_day_limit': false };
            if (template.template_context === 'commerce-7-transaction-email-account-activation') return { 'label': 'Account Confirmation', 'fourteen_day_limit': false };

            return null;
        },

        link_shortener_host_choices() {
            let ret = [];

            ret.push({ text: 'Use default link shortener', value: null });
            ret.push({ text: 'Do not shorten links', value: 'do-no-shorten-link-under-any-circumstances.biz' });

            let hosts = this.link_shortener_hosts;
            if (hosts == null) return ret;

            for (let i = 0; i < hosts.length; i++) {
                if (hosts[i].is_active == false) continue;

                ret.push({ text: hosts[i].host, value: hosts[i].host });
            }

            return ret;
        },

        klaviyo_webhook_url() {
            const organization_id = this.$route.params.organization_id;
            return `https://app.redchirp.com/api/v1/webhook/inbound/klaviyo/${organization_id}`;
        },

        custom_webhook_url() {
            return `https://app.redchirp.com/api/v1/external/automated-messages/trigger/${this.template.template_subject_id}`;
        },

        mailchimp_campaigns() {
            if (this.template == null || this.template.email_campaign_ids == null || this.template.email_campaign_labels == null || this.template.email_campaign_ids.length != this.template.email_campaign_labels.length)
                return [];

            const ret = [];

            for (let i = 0; i < this.template.email_campaign_ids.length; i++) {
                ret.push({
                    text: this.template.email_campaign_labels[i],
                    value: this.template.email_campaign_ids[i],
                });
            }

            return ret;
        },

        is_ecellar_enabled() {
            return this.$rc.$state.isCRMEnabled('ecellar');
        },

        is_reservation_message() {
            if (this.template == null) return false;

            const reservation_contexts = [
                'auto-message-reservation-ecellar-booked',
                'auto-message-reservation-ecellar-requested',
                'auto-message-reservation-ecellar-visited',
                'auto-message-reservation-ecellar-tentative',
                'auto-message-reservation-reminder-almost-time',
                'auto-message-reservation-reminder-morning-of',
                'auto-message-reservation-reminder-day-before',
                'auto-message-reservation-reminder-two-days-before',
                'auto-message-reservation-at-start-time',
                'auto-message-reservation-day-after',
                'auto-message-reservation-reminder-week-before',
                'auto-message-reservation-reminder-month-before'
            ];

            return reservation_contexts.includes(this.template.template_context);
        },

        is_second_generation_shipping_message() {
            if (this.template == null) return false;

            return ['shipping-exception', 'shipping-new-tracking-number', 'shipping-on-weather-hold', 'shipping-weather-hold-release', 'shipping-first-in-transit', 'shipping-out-for-delivery', 'shipping-picked-up', 'shipping-delivery-attempt-failed', 'shipping-available-for-pickup', 'shipping-delivered', 'shipping-expected-delivery-date-changed'].includes(this.template.template_context);
        },

        shipping_trigger_concept() {
            const context = this.template.template_context;

            if (context === 'shipping-new-tracking-number') return 'can-trigger-shipping-new-tracking-number';
            if (context === 'shipping-on-weather-hold') return 'can-trigger-shipping-on-weather-hold';
            if (context === 'shipping-weather-hold-release') return 'can-trigger-shipping-weather-hold-release';
            if (context === 'shipping-first-in-transit') return 'can-trigger-shipping-first-in-transit';
            if (context === 'shipping-out-for-delivery') return 'can-trigger-shipping-out-for-delivery';
            if (context === 'shipping-picked-up') return 'can-trigger-shipping-picked-up';
            if (context === 'shipping-delivery-attempt-failed') return 'can-trigger-shipping-delivery-attempt-failed';
            if (context === 'shipping-available-for-pickup') return 'can-trigger-shipping-available-for-pickup';
            if (context === 'shipping-delivered') return 'can-trigger-shipping-delivered';
            if (context === 'shipping-expected-delivery-date-changed') return 'can-trigger-shipping-expected-delivery-date-changed';

            return null;
        },

        shipping_limit_once_per_day() {
            const context = this.template.template_context;
            return [
                'shipping-out-for-delivery',
                'shipping-delivery-attempt-failed',
                'shipping-available-for-pickup'
            ].includes(context);
        },

        back_route() {
            // Use router.replace with a timestamp query parameter to force refresh
            // and prevent cached versions from being shown
            return {
                name: 'auto-messages-settings',
                params: {
                    organization_id: this.$route.params.organization_id
                },
                query: {
                    _refresh: Date.now()
                }
            };
        },
    },
}
</script>

<style lang="scss" scoped>
.question,
.answer {
    font-weight: bold;
    margin-top: 8px;
}

.question-text,
.answer-text {
    margin-left: 15px;
    margin-bottom: 8px;
}
</style>